import { useEffect, useState, useRef } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { verifyOTP, resendOTP } from '@/lib/api/auth'
import { useAuthContext } from '@/contexts/auth-context'
import { toast } from 'sonner'

const HoomiLogo = '/assets/hoomi-logo.png'

export default function VerifyOTP() {
  const navigate = useNavigate()
  const location = useLocation()
  const { login } = useAuthContext()
  const email = location.state?.email || 'your@email.com'
  const redirectionUrl: string | null = location.state?.redirectionUrl ?? null
  const platform: string | null = location.state?.platform ?? null
  
  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [isLoading, setIsLoading] = useState(false)
  const [resendTimer, setResendTimer] = useState(60)
  const [canResend, setCanResend] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  
  useEffect(() => {
    // Focus first input on mount
    inputRefs.current[0]?.focus()
  }, [])
  
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000)
      return () => clearTimeout(timer)
    } else {
      setCanResend(true)
    }
  }, [resendTimer])
  
  const handleOtpChange = (index: number, value: string) => {
    // Only allow numbers
    if (value && !/^\d+$/.test(value)) return
    
    const newOtp = [...otp]
    newOtp[index] = value.slice(-1) // Only take the last character
    setOtp(newOtp)
    
    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }
  
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }
  
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').slice(0, 6)
    if (!/^\d+$/.test(pastedData)) return
    
    const newOtp = [...otp]
    pastedData.split('').forEach((char, index) => {
      if (index < 6) newOtp[index] = char
    })
    setOtp(newOtp)
    
    // Focus the next empty input or the last one
    const nextIndex = Math.min(pastedData.length, 5)
    inputRefs.current[nextIndex]?.focus()
  }
  
  const handleVerify = async () => {
    const otpCode = otp.join('')
    
    if (otpCode.length !== 6) {
      toast.error('Please enter all 6 digits')
      return
    }
    
    setIsLoading(true)
    
    try {
      const result = await verifyOTP({ email, otp: otpCode })

      const token = result.data?.token
      if (token) {
        login(token)
      }

      toast.success('Email verified successfully!')

      if (redirectionUrl) {
        // Desktop web with redirect: session_token cookie set via login() → redirect directly
        window.location.href = redirectionUrl
      } else if (platform === 'mobile') {
        // Mobile WebView: pass jwt_token in URL for native app to capture, then auto-close
        const params = new URLSearchParams()
        if (token) params.set('jwt_token', token)
        params.set('platform', 'mobile')
        navigate(`/?${params.toString()}`)
      } else {
        // Desktop no-redirect: go straight to /authorized
        navigate('/authorized')
      }
    } catch (error: any) {
      console.error('OTP verification error:', error)
      toast.error(error.message || 'Invalid OTP. Please try again.')
      // Clear OTP inputs
      setOtp(['', '', '', '', '', ''])
      inputRefs.current[0]?.focus()
    } finally {
      setIsLoading(false)
    }
  }
  
  const handleResend = async () => {
    if (!canResend) return
    
    try {
      await resendOTP({ email })
      toast.success('OTP sent to your email')
      setResendTimer(60)
      setCanResend(false)
    } catch (error: any) {
      console.error('Resend OTP error:', error)
      toast.error(error.message || 'Failed to resend OTP')
    }
  }
  
  const isOtpComplete = otp.every(digit => digit !== '')
  
  return (
    <div className="bg-black flex min-h-svh flex-col items-center justify-center gap-6 p-6 md:p-10 text-white relative">
      {/* Purple gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-purple-950/30 to-black z-0"></div>

      {/* Purple accent elements */}
      <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-purple-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-purple-500/10 rounded-full blur-3xl"></div>

      {/* Header with logo */}
      <div className="absolute top-0 left-0 w-full p-4 flex items-center">
        <Link to="/" className="flex items-center gap-2">
          <img src={HoomiLogo} alt="Hoomi DePIN" width={24} height={24} />
          <span className="font-semibold text-purple-300">Hoomi Social</span>
        </Link>
      </div>

      {/* OTP verification container */}
      <div className="w-full max-w-md z-10 flex flex-col items-center gap-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-white mb-2">
            To make sure it's you, please verify your Email
          </h1>
        </div>
        
        {/* OTP Input boxes */}
        <div className="flex gap-3 justify-center" onPaste={handlePaste}>
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => {
                inputRefs.current[index] = el
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className="w-14 h-14 text-center text-2xl font-semibold bg-white/90 text-gray-900 rounded-xl border-0 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
              disabled={isLoading}
            />
          ))}
        </div>
        
        {/* Continue button */}
        <Button
          onClick={handleVerify}
          disabled={!isOtpComplete || isLoading}
          className="w-full max-w-md bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white h-14 rounded-xl font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <div className="flex items-center">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent mr-2"></div>
              Verifying...
            </div>
          ) : (
            'Continue'
          )}
        </Button>
        
        {/* Email info */}
        <div className="text-center">
          <p className="text-white text-sm mb-2">
            We have sent a 6 digit OTP to
          </p>
          <p className="text-white font-semibold">{email}</p>
        </div>
        
        {/* Resend button */}
        <button
          onClick={handleResend}
          disabled={!canResend}
          className={`text-white underline text-base font-medium transition-colors ${
            canResend ? 'hover:text-purple-300 cursor-pointer' : 'opacity-50 cursor-not-allowed'
          }`}
        >
          Resend Email
        </button>
        
        {/* Resend timer */}
        {!canResend && (
          <p className="text-white/70 text-sm">
            Email resent, you can send another one in {resendTimer} seconds
          </p>
        )}
      </div>
    </div>
  )
}
